import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Alert,
  Button,
  useTheme,
  alpha,
  Divider,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ComposedChart,
} from 'recharts';
import { useTicket } from '../contexts/TicketContext';
import { Refresh, Analytics, TrendingUp, CheckCircle, Schedule, BugReport, Business } from '@mui/icons-material';

const Reports: React.FC = () => {
  const { reportData, reportLoading, getReportData } = useTicket();
  const theme = useTheme();
  const [dataLoaded, setDataLoaded] = useState(false);

  // 🔽 EK: firma listesi için gösterim kontrolü
  const [showAllCompanies, setShowAllCompanies] = useState(false);

  useEffect(() => {
    if (!dataLoaded && !reportLoading) {
      getReportData();
      setDataLoaded(true);
    }
  }, [dataLoaded, reportLoading, getReportData]);

  // Modern renk paleti
  const COLOR_PALETTE = {
    primary: '#2563eb',
    success: '#059669',
    warning: '#d97706',
    error: '#dc2626',
    info: '#0369a1',
    purple: '#8b5cf6',
  };

  // Tooltip - Sade ve net
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 3, bgcolor: entry.color, borderRadius: 1 }} />
              <Typography variant="body2">
                {entry.name}: <strong>{entry.value}{entry.dataKey === 'cozulmeOrani' ? '%' : ''}</strong>
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  // Pie Tooltip
  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            {payload[0].name}
          </Typography>
          <Typography variant="body1" fontWeight="bold" color={payload[0].color}>
            {data.value} adet ({data.percentage}%)
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  // Safe data access
  const { 
    monthlyData = [], 
    statusData = [], 
    moduleData = [], 
    companyData = [], // ✅ FİRMA VERİSİ
    generalStats = {
      totalTickets: 0,
      resolutionRate: 0,
      avgResolutionTime: 0,
      activeTickets: 0
    }
  } = reportData || {};

  // ✅ PIE CHART VERİLERİNE YÜZDE EKLE
  const calculatePercentages = (data: any[]) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    return data.map(item => ({
      ...item,
      percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : '0'
    }));
  };

  const statusDataWithPercentages = calculatePercentages(statusData);
  const moduleDataWithPercentages = calculatePercentages(moduleData);
  const companyDataWithPercentages = calculatePercentages(companyData); // ✅ FİRMA YÜZDELERİ

  // ✅ PIE CHART LABEL - YÜZDE YAZSIN
  const renderCustomizedLabel = ({
    cx, cy, midAngle, innerRadius, outerRadius, percentage
  }: any) => {
    if (percentage === '0.0') return null;
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${percentage}%`}
      </text>
    );
  };

  if (reportLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Analytics sx={{ fontSize: 48, color: 'primary.main', mb: 2, opacity: 0.7 }} />
          <Typography variant="h6">Raporlar yükleniyor...</Typography>
        </Box>
      </Box>
    );
  }

  const hasData = monthlyData.length > 0 || statusData.length > 0 || moduleData.length > 0 || companyData.length > 0;

  if (!hasData) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            📊 Raporlar ve Analizler
          </Typography>
        </Box>
        <Paper sx={{ p: 3 }}>
          <Alert severity="info">Henüz rapor verisi bulunmuyor.</Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* HEADER */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          📊 Raporlar ve Analizler
        </Typography>
      </Box>

      {/* STAT KARTLARI */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { 
            value: generalStats.totalTickets.toLocaleString(), 
            label: 'Toplam Talep', 
            color: COLOR_PALETTE.primary, 
            icon: <BugReport />,
          },
          { 
            value: `${generalStats.resolutionRate}%`, 
            label: 'Çözülme Oranı', 
            color: COLOR_PALETTE.success, 
            icon: <CheckCircle />,
          },
          { 
            value: `${generalStats.avgResolutionTime} Gün`, 
            label: 'Ort. Çözüm Süresi', 
            color: COLOR_PALETTE.warning, 
            icon: <Schedule />,
          },
          { 
            value: generalStats.activeTickets.toLocaleString(), 
            label: 'Aktif Talep', 
            color: COLOR_PALETTE.info, 
            icon: <TrendingUp />,
          }
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${alpha(stat.color, 0.1)} 0%, ${alpha(stat.color, 0.05)} 100%)`,
              border: `1px solid ${alpha(stat.color, 0.2)}`,
              borderRadius: 2,
              height: 120,
              display: 'flex',
              alignItems: 'center',
            }}>
              <CardContent sx={{ 
                p: 3, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                width: '100%'
              }}>
                <Box sx={{ 
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  bgcolor: alpha(stat.color, 0.2),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Box sx={{ color: stat.color, fontSize: 24 }}>
                    {stat.icon}
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" fontWeight="bold" color={stat.color}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                    {stat.label}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* PIE CHART'LAR - ÜST SIRADA */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* TALEP DURUMLARI */}
        {statusData.length > 0 && (
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                📊 Talep Durumları
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Taleplerin mevcut durum dağılımı
              </Typography>
              
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusDataWithPercentages}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={renderCustomizedLabel}
                    labelLine={false}
                  >
                    {statusDataWithPercentages.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={[
                          COLOR_PALETTE.primary,
                          COLOR_PALETTE.warning,
                          COLOR_PALETTE.success,
                          COLOR_PALETTE.info,
                          COLOR_PALETTE.error
                        ][index % 5]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>

              {/* DURUM LİSTESİ */}
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column' }}>
                {statusDataWithPercentages.map((item, index) => (
                  <Box key={`${item.name}-${index}`}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: [
                              COLOR_PALETTE.primary,
                              COLOR_PALETTE.warning,
                              COLOR_PALETTE.success,
                              COLOR_PALETTE.info,
                              COLOR_PALETTE.error
                            ][index % 5]
                          }} 
                        />
                        <Typography variant="body2" fontWeight="500">
                          {item.name}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {item.value} adet
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.percentage}%
                        </Typography>
                      </Box>
                    </Box>
                    {index < statusDataWithPercentages.length - 1 && (
                      <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.3), my: 0.5 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* MODÜL DAĞILIMI */}
        {moduleData.length > 0 && (
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                🏗️ Modül Dağılımı
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Taleplerin modül bazında dağılımı
              </Typography>
              
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={moduleDataWithPercentages}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={renderCustomizedLabel}
                    labelLine={false}
                  >
                    {moduleDataWithPercentages.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={[
                          COLOR_PALETTE.primary,
                          COLOR_PALETTE.success,
                          COLOR_PALETTE.warning,
                          COLOR_PALETTE.info,
                          COLOR_PALETTE.purple
                        ][index % 5]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>

              {/* MODÜL LİSTESİ */}
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column' }}>
                {moduleDataWithPercentages.map((item, index) => (
                  <Box key={`${item.name}-${index}`}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: [
                              COLOR_PALETTE.primary,
                              COLOR_PALETTE.success,
                              COLOR_PALETTE.warning,
                              COLOR_PALETTE.info,
                              COLOR_PALETTE.purple
                            ][index % 5]
                          }} 
                        />
                        <Typography variant="body2" fontWeight="500">
                          {item.name}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {item.value} adet
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.percentage}%
                        </Typography>
                      </Box>
                    </Box>
                    {index < moduleDataWithPercentages.length - 1 && (
                      <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.3), my: 0.5 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* ✅ FİRMA DAĞILIMI - LİSTE SINIRLI (6) */}
        {companyData.length > 0 && (
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                🏢 Firma Talep Dağılımı
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Hangi firmadan kaç talep var
              </Typography>
              
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={companyDataWithPercentages}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={renderCustomizedLabel}
                    labelLine={false}
                  >
                    {companyDataWithPercentages.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={[
                          COLOR_PALETTE.primary,
                          COLOR_PALETTE.success,
                          COLOR_PALETTE.warning,
                          COLOR_PALETTE.info,
                          COLOR_PALETTE.purple
                        ][index % 5]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>

              {/* FİRMA LİSTESİ - EN FAZLA 6 GÖSTER */}
              {(() => {
                const MAX_COMPANY_ROWS = 6;
                const companyList = showAllCompanies
                  ? companyDataWithPercentages
                  : companyDataWithPercentages.slice(0, MAX_COMPANY_ROWS);
                const hiddenCount = Math.max(companyDataWithPercentages.length - MAX_COMPANY_ROWS, 0);

                return (
                  <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column' }}>
                    {companyList.map((item, index) => (
                      <Box key={`${item.name}-${index}`}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                bgcolor: [
                                  COLOR_PALETTE.primary,
                                  COLOR_PALETTE.success,
                                  COLOR_PALETTE.warning,
                                  COLOR_PALETTE.info,
                                  COLOR_PALETTE.purple
                                ][index % 5]
                              }} 
                            />
                            <Typography variant="body2" fontWeight="500">
                              {item.name}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight="bold">
                              {item.value} adet
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.percentage}%
                            </Typography>
                          </Box>
                        </Box>
                        {index < companyList.length - 1 && (
                          <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.3), my: 0.5 }} />
                        )}
                      </Box>
                    ))}

                    {hiddenCount > 0 && (
                      <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setShowAllCompanies(s => !s)}
                        >
                          {showAllCompanies ? 'Daralt' : `Hepsini Göster (+${hiddenCount})`}
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* AYLIK TALEP DAĞILIMI */}
      {monthlyData.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, mt: 9 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            📈 Aylık Talep Dağılımı - {new Date().getFullYear()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Her ay için toplam talep ve çözülen talep sayıları
          </Typography>
          
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }}
                axisLine={false}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="talepler" 
                fill={COLOR_PALETTE.primary} 
                name="Toplam Talep"
                radius={[2, 2, 0, 0]}
                barSize={30}
              />
              <Bar 
                dataKey="cozulen" 
                fill={COLOR_PALETTE.success} 
                name="Çözülen Talep"
                radius={[2, 2, 0, 0]}
                barSize={30}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {/* RENK AÇIKLAMALARI */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 4, bgcolor: COLOR_PALETTE.primary, borderRadius: 1 }} />
              <Typography variant="body2" fontWeight="500">Toplam Talep</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 4, bgcolor: COLOR_PALETTE.success, borderRadius: 1 }} />
              <Typography variant="body2" fontWeight="500">Çözülen Talep</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* ÇÖZÜLME ORANI TRENDİ */}
      {monthlyData.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            📉 Aylık Çözülme Oranı Trendi
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Aylara göre çözülme oranı değişimi
          </Typography>
          
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }}
                axisLine={false}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                axisLine={false}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="cozulmeOrani" 
                stroke={COLOR_PALETTE.purple}
                strokeWidth={3}
                dot={{ fill: COLOR_PALETTE.purple, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: COLOR_PALETTE.purple }}
                name="Çözülme Oranı"
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
};

export default Reports;
